import * as React from 'react';
import { useLocation } from 'react-router-dom';

/** Header top-padding ceiling at lg+ in px (matches Figma `spacing-6xl`). */
const HEADER_PT_MAX_PX = 48;
/** Header top-padding floor in px (matches Figma `spacing-xl`). */
const HEADER_PT_MIN_PX = 16;
/** Content height, in px, the header sheds when it snaps shut. */
export const HEADER_SHRINK_RANGE_PX = HEADER_PT_MAX_PX - HEADER_PT_MIN_PX;
/** scrollTop, in px, past which the header snaps to its shrunk state. */
export const HEADER_SHRINK_THRESHOLD_PX = 16;

interface IScrollMetrics {
  scrollHeight: number;
  clientHeight: number;
  scrollTop: number;
  /** Last progress written; used to recover the natural (progress=0) scroll extent. */
  prevProgress: number;
}

/**
 * Maps a scroller's position to the header-shrink progress — binary: 0
 * (expanded) until scrollTop crosses HEADER_SHRINK_THRESHOLD_PX, then 1
 * (shrunk). The CSS side animates the snap with a transition.
 *
 * Returns 0 (shrink disabled) when the natural scroll runway can't absorb a
 * full shrink without snapping back: shrinking the header removes
 * HEADER_SHRINK_RANGE_PX of content height, so on a runway shorter than 2× that
 * the bottom-clamped scrollTop could land back under the threshold, re-expand
 * the header, and oscillate. (2× leaves the clamp at ≥ HEADER_SHRINK_RANGE_PX,
 * which is above the threshold.) Recover the natural runway by adding back the
 * shrink already applied (prevProgress × range) before testing it.
 */
export const computeHeaderScrollProgress = ({
  scrollHeight,
  clientHeight,
  scrollTop,
  prevProgress,
}: IScrollMetrics): number => {
  const currentScrollable = scrollHeight - clientHeight;
  const naturalScrollable = currentScrollable + prevProgress * HEADER_SHRINK_RANGE_PX;
  if (naturalScrollable < 2 * HEADER_SHRINK_RANGE_PX) {
    return 0;
  }
  return scrollTop > HEADER_SHRINK_THRESHOLD_PX ? 1 : 0;
};

/**
 * Drives EntityPaneHeader's scroll-shrink from the single full-width pane
 * scroller (see scenes/HomeScene.tsx). Attach the returned ref + onScroll to
 * that scroller: on every scroll it writes `--entity-header-scroll-progress`
 * (0 | 1) onto the scroller element, which cascades down to the sticky header's
 * `entity-header-scroll-shrink` utility (see styles/app.css). Resets to the top
 * on every navigation — html/body are locked (ORN-548), so the canonical
 * scroller is here and the browser's own scroll restoration can't reach it.
 *
 * @returns `scrollerRef` to attach to the scroll container and `onScroll` to
 * wire as its scroll handler.
 */
export const useHeaderScrollShrink = () => {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const progressRef = React.useRef(0);
  const location = useLocation();

  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller != null) {
      scroller.scrollTop = 0;
      scroller.style.setProperty('--entity-header-scroll-progress', '0');
    }
    progressRef.current = 0;
  }, [location.key]);

  const onScroll = React.useCallback(() => {
    const scroller = scrollerRef.current;
    if (scroller == null) {
      return;
    }
    const progress = computeHeaderScrollProgress({
      scrollHeight: scroller.scrollHeight,
      clientHeight: scroller.clientHeight,
      scrollTop: scroller.scrollTop,
      prevProgress: progressRef.current,
    });
    scroller.style.setProperty('--entity-header-scroll-progress', String(progress));
    progressRef.current = progress;
  }, []);

  return { scrollerRef, onScroll };
};
